import { Link } from "react-router-dom";
import { Settings, ShieldCheck } from "lucide-react";
import type { ShellApp } from "../../lib/apps";

// Apps mit eigenem Iframe-Slot — Klick auf das bereits aktive Rail-Icon soll
// das Iframe neu laden (vs. Hub-eigene Pages, die als SPA-Re-Render reichen).
const IFRAME_KINDS: ReadonlyArray<ShellApp["kind"]> = ["satellite", "static"];

// =====================================================================
// Rail — vertikale App-Leiste.
//   * `visibleApps` enthaelt bereits die RBAC-gefilterten Eintraege; die
//     Reihenfolge entspricht der Reihenfolge in SHELL_APPS (= 1..7 Hotkey).
//   * Hub-internes Design-System wurde aus der Rail entfernt — erreichbar
//     via /static/design-system.html direkt.
//   * Hub-Icon wurde aus der Rail entfernt — Hub ist nur über den
//     COMBSOL/OS-Button oben links erreichbar.
  //   * Administration (Rail-Icon Cyan): GF-only — Sichtbarkeit wird von
  //     AppShell anhand der bestehenden GF-Prüfung gesteuert.
// =====================================================================

type Props = {
  activeId: string;
  visibleApps: ShellApp[];
  showAdministration?: boolean;
};

function RailItem({
  app,
  isActive,
  shortcutIndex,
}: {
  app: ShellApp;
  isActive: boolean;
  shortcutIndex: number;
}) {
  const kbd = String(shortcutIndex + 1);
  const className = `rail-btn ${isActive ? "is-active" : ""}`;
  const title = kbd ? `${app.title} (${kbd})` : app.title;
  const inner = (
    <>
      <img
        src={app.iconSrc}
        alt=""
        width={40}
        height={40}
        draggable={false}
      />
      <span className="rail-label">{app.title}</span>
    </>
  );

  if (app.kind === "external" && app.externalUrl) {
    return (
      <a
        href={app.externalUrl}
        className={className}
        data-app={app.id}
        title={title}
        target="_blank"
        rel="noopener noreferrer"
      >
        {inner}
      </a>
    );
  }

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Re-Klick auf bereits aktive Satellite-App: Route bleibt gleich, React-
    // Router triggert keinen Remount → das Iframe wuerde einfach stehen
    // bleiben. Stattdessen ein Custom-Event feuern, das die SatelliteView
    // abfaengt und das Iframe auf defaultEmbedSrc zuruecksetzt.
    if (isActive && IFRAME_KINDS.includes(app.kind)) {
      e.preventDefault();
      window.dispatchEvent(
        new CustomEvent("hub-satellite-reload", { detail: { appId: app.id } }),
      );
    }
  };

  return (
    <Link
      to={app.route}
      className={className}
      data-app={app.id}
      title={title}
      onClick={handleClick}
    >
      {inner}
    </Link>
  );
}

export function Rail({ activeId, visibleApps, showAdministration = false }: Props) {
  // Hub-App aus der Rail herausfiltern — Hub ist nur über den COMBSOL/OS-Button
  // oben links erreichbar. Design System wurde ebenfalls entfernt.
  const railApps = visibleApps.filter(
    (app) => app.id !== "hub" && app.showInRail !== false,
  );
  return (
    <nav className="os-rail" aria-label="App-Launcher">
      <div className="rail-divider" />

      {railApps.map((app, index) => (
        <RailItem
          key={app.id}
          app={app}
          isActive={activeId === app.id}
          shortcutIndex={index}
        />
      ))}

      <div className="rail-spacer" />

      {showAdministration ? (
        <Link
          to="/admin"
          className={`rail-btn ${activeId === "admin" ? "is-active" : ""}`}
          data-app="admin"
          title="Administration"
          data-testid="rail-admin"
        >
          <ShieldCheck size={17} strokeWidth={1.7} />
          <span className="rail-label">Administration</span>
        </Link>
      ) : null}

      <Link
        to="/hub/einstellungen"
        className={`rail-btn ${activeId === "settings" ? "is-active" : ""}`}
        title="Persönliche Einstellungen"
      >
        <Settings size={17} strokeWidth={1.7} />
        <span className="rail-label">Einstellungen</span>
      </Link>
    </nav>
  );
}
